import ClockoutController from '../../../core/clockout.controller';
import Inherit from '../../../core/inherit';
import { navbarSections } from './navbar.constants';
import { NAVBAR_ACTIVE_PATH_ALIASES } from './navbar.paths';
import { userHasScope } from '../../../constants/scopes.constants';

class NewSidebarController extends Inherit(ClockoutController) {
    /**
     * @param {typeof navbarSections} [sections]
     * @returns {Set<string>}
     */
    collectNavbarPaths(sections = navbarSections) {
        const paths = new Set();

        const walk = (items) => {
            for (const item of items) {
                if (item.path) {
                    paths.add(item.path);
                }
                if (Array.isArray(item.subsections)) {
                    walk(item.subsections);
                }
            }
        };

        walk(sections);
        return paths;
    }

    /**
     * Sections the user may see: scoped entries need their scope, unscoped
     * ones (Settings) always show. Divider rows survive only between two
     * visible items, so a hidden group never leaves a stray or doubled rule.
     *
     * @param {Array<object>} sections
     * @param {object} [user]
     * @returns {Array<object>}
     */
    getVisibleSections(sections, user) {
        const visible = sections.filter((section) =>
            section.type || !section.requiredScope || userHasScope(user, section.requiredScope)
        );

        return visible.filter((section, index) => {
            if (!section.type) return true;
            const previous = visible[index - 1];
            const next = visible[index + 1];
            return Boolean(previous && !previous.type && next && !next.type);
        });
    }

    /**
     * Sidebar items the user may see.
     *
     * @param {object} [user]
     * @returns {Array<object>}
     */
    getSections(user) {
        return this.getVisibleSections(navbarSections, user);
    }

    /**
     * @param {{ pathname?: string } | null | undefined} location
     * @returns {string}
     */
    getNavbarCurrentPath(location) {
        return location?.pathname || window.location.pathname || '';
    }

    /**
     * Path passed to NavigationBar as `currentPath`.
     * NavigationBar already prefix-matches section.path against the URL
     * (e.g. /configurations/foo → /configurations, /support/ticket → /support).
     * Legacy routes that sit outside those prefixes are remapped via aliases.
     *
     * @param {{ pathname?: string } | null | undefined} location
     * @returns {string}
     */
    resolveNavbarActivePath(location) {
        const pathname = this.getNavbarCurrentPath(location);
        return NAVBAR_ACTIVE_PATH_ALIASES[pathname] ?? pathname;
    }

    /**
     * @param {string} path
     * @param {typeof navbarSections} [sections]
     * @returns {boolean}
     */
    isNavbarRoute(path, sections = navbarSections) {
        return path != null && this.collectNavbarPaths(sections).has(path);
    }

    /**
     * @param {string} path — from {@link navbarSections} entry `path`
     * @param {object} [section]
     */
    handleNavigate(path, section) {
        if (section?.onClick) {
            section.onClick(section);
            return;
        }
        if (!path || !this.isNavbarRoute(path)) {
            return;
        }
        if (this.props.navigate) {
            this.props.navigate(path);
        }
    }
}

export default NewSidebarController;
